import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import CloseIcon from '@mui/icons-material/Close'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import EditIcon from '@mui/icons-material/Edit'
import OutputIcon from '@mui/icons-material/Output'
import SortIcon from '@mui/icons-material/Sort'
import Swal from 'sweetalert2'

import {
    deleteSalidaProduccion,
    getAlmacenes,
    getSalidasProduccion,
    getTiposMaquinas,
    patchSalidaProduccion,
    postSalidaProduccion,
} from '../services/apis'
import { LoadingMaestro } from '../components/LoadingMaestro'
import { normalizarArray } from '../utils/apiResponse'

const getApiErrorMessage = (error, fallback) => {
    const message = error.response?.data?.message

    if (Array.isArray(message)) {
        return message.join('\n')
    }

    return message || fallback
}

const salidaInicial = {
    desMaquina: '',
    codAlmacen: '',
    tipoMaquina: '',
    activa: true,
    ordenVisual: 1,
}

export const SalidasProduccion = () => {
    const [salidasProduccion, setSalidasProduccion] = useState([])
    const [almacenes, setAlmacenes] = useState([])
    const [tiposMaquinas, setTiposMaquinas] = useState([])
    const [filtroAlmacen, setFiltroAlmacen] = useState('todos')
    const [filtroTipoMaquina, setFiltroTipoMaquina] = useState('todos')
    const [filtroActiva, setFiltroActiva] = useState('todos')
    const [loading, setLoading] = useState(true)
    const [openSalida, setOpenSalida] = useState(false)
    const [salidaEditandoCod, setSalidaEditandoCod] = useState(null)
    const [salidaEliminar, setSalidaEliminar] = useState(null)
    const [salidaForm, setSalidaForm] = useState(salidaInicial)

    const cargarDatos = useCallback(async () => {
        setLoading(true)
        try {
            const [salidasResponse, almacenesResponse, tiposResponse] = await Promise.all([
                getSalidasProduccion(),
                getAlmacenes(),
                getTiposMaquinas(),
            ])
            const salidasData = normalizarArray(salidasResponse, 'salidasProduccion')

            setSalidasProduccion(salidasData)
            setAlmacenes(normalizarArray(almacenesResponse, 'almacenes'))
            setTiposMaquinas(normalizarArray(tiposResponse, 'tiposMaquinas'))
        } catch (error) {
            console.error('Error al cargar salidas de produccion:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: getApiErrorMessage(error, 'Error al cargar las salidas de produccion'),
                timer: 2500,
                showConfirmButton: false,
            })
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        cargarDatos()
    }, [cargarDatos])

    const almacenesPorCodigo = useMemo(() => (
        almacenes.reduce((acc, almacen) => {
            acc[almacen.codAlmacen] = almacen.desAlmacen
            return acc
        }, {})
    ), [almacenes])

    const tiposPorCodigo = useMemo(() => (
        tiposMaquinas.reduce((acc, tipoMaquina) => {
            acc[tipoMaquina.codTipoMaquina] = tipoMaquina.desTipoMaquina
            return acc
        }, {})
    ), [tiposMaquinas])

    const salidasFiltradas = useMemo(() => (
        [...salidasProduccion]
            .filter((salida) => (
                filtroAlmacen === 'todos' || String(salida.codAlmacen) === String(filtroAlmacen)
            ))
            .filter((salida) => (
                filtroTipoMaquina === 'todos' || Number(salida.tipoMaquina) === Number(filtroTipoMaquina)
            ))
            .filter((salida) => {
                if (filtroActiva === 'todos') return true
                return Boolean(salida.activa) === (filtroActiva === 'activa')
            })
            .sort((salidaA, salidaB) => {
                const ordenA = Number(salidaA.ordenVisual ?? 0)
                const ordenB = Number(salidaB.ordenVisual ?? 0)

                if (ordenA !== ordenB) return ordenA - ordenB

                return Number(salidaA.codMaquina ?? 0) - Number(salidaB.codMaquina ?? 0)
            })
    ), [filtroActiva, filtroAlmacen, filtroTipoMaquina, salidasProduccion])

    const resetSalidaForm = () => {
        setSalidaForm(salidaInicial)
    }

    const abrirCrearSalida = () => {
        resetSalidaForm()
        setSalidaEditandoCod(null)
        setOpenSalida(true)
    }

    const abrirEditarSalida = (salida) => {
        setSalidaForm({
            desMaquina: salida.desMaquina ?? '',
            codAlmacen: salida.codAlmacen ?? '',
            tipoMaquina: salida.tipoMaquina ?? '',
            activa: salida.activa ?? true,
            ordenVisual: salida.ordenVisual ?? 1,
        })
        setSalidaEditandoCod(salida.codMaquina)
        setOpenSalida(true)
    }

    const cerrarSalida = () => {
        setOpenSalida(false)
        setSalidaEditandoCod(null)
        resetSalidaForm()
    }

    const handleChangeSalida = (event) => {
        const { name, value, checked, type } = event.target

        setSalidaForm((salidaActual) => ({
            ...salidaActual,
            [name]: type === 'checkbox' ? checked : value,
        }))
    }

    const guardarSalida = async (event) => {
        event.preventDefault()

        const salidaEnvio = {
            desMaquina: salidaForm.desMaquina.trim(),
            codAlmacen: String(salidaForm.codAlmacen),
            tipoMaquina: Number(salidaForm.tipoMaquina),
            activa: Boolean(salidaForm.activa),
            ordenVisual: Number(salidaForm.ordenVisual),
        }

        try {
            if (salidaEditandoCod) {
                await patchSalidaProduccion(salidaEditandoCod, salidaEnvio)
            } else {
                await postSalidaProduccion(salidaEnvio)
            }

            cerrarSalida()
            cargarDatos()
            Swal.fire({
                icon: 'success',
                title: salidaEditandoCod ? 'Salida actualizada' : 'Salida creada',
                showConfirmButton: false,
                timer: 1500,
            })
        } catch (error) {
            console.error('Error al guardar la salida de produccion:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: getApiErrorMessage(error, 'Error al guardar la salida de produccion'),
                timer: 2500,
                showConfirmButton: false,
            })
        }
    }

    const confirmarEliminarSalida = async () => {
        if (!salidaEliminar) return

        try {
            await deleteSalidaProduccion(salidaEliminar.codMaquina)
            setSalidaEliminar(null)
            cargarDatos()
            Swal.fire({
                icon: 'success',
                title: 'Salida eliminada',
                showConfirmButton: false,
                timer: 1500,
            })
        } catch (error) {
            console.error('Error al eliminar la salida de produccion:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: getApiErrorMessage(error, 'Error al eliminar la salida de produccion'),
                timer: 2500,
                showConfirmButton: false,
            })
        }
    }

    return (
        <Grid container direction="row" size={{ xs: 12 }} sx={{ justifyContent: "center", alignItems: "center" }}>
            <Grid container direction="row" size={{ xs: 10 }} sx={{ justifyContent: "start", alignItems: "start", mt: 3 }} className='fade-in ajustes-compact' spacing={2}>
                <Grid container direction="row" size={{ xs: 12 }} sx={{ justifyContent: "space-between", alignItems: "center" }} spacing={2}>
                    <Grid container direction="column" size={{ xs: 12, md: 6 }} sx={{ alignItems: "start" }}>
                        <Typography variant='h4'>
                            Salidas produccion
                        </Typography>

                        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                            Maestro de salidas de produccion por almacen y tipo de maquina.
                        </Typography>
                    </Grid>

                    <Grid container size={{ xs: 12, sm: 5, md: 3 }} className='card-main' sx={{ p: 1.5, alignItems: "center" }}>
                        <OutputIcon color="primary" sx={{ mr: 1 }} />
                        <Box>
                            <Typography variant="caption">Salidas filtradas</Typography>
                            <Typography variant="h5">{salidasFiltradas.length}</Typography>
                        </Box>
                    </Grid>

                    <Grid container direction="row" size={{ xs: 12, sm: 5, md: 2 }} sx={{ justifyContent: { xs: "start", sm: "end" } }}>
                        <Button variant="contained" fullWidth onClick={abrirCrearSalida}>
                            <AddCircleIcon />&nbsp;Nueva salida
                        </Button>
                    </Grid>

                    <Divider />
                </Grid>

                <Grid container direction="row" size={{ xs: 12 }} spacing={2} sx={{ alignItems: "center" }}>
                    <Grid container size={{ xs: 12, md: 4 }}>
                        <FormControl fullWidth>
                            <InputLabel>Almacen</InputLabel>
                            <Select label="Almacen" value={filtroAlmacen} onChange={(event) => setFiltroAlmacen(event.target.value)}>
                                <MenuItem value="todos">Todos</MenuItem>
                                {almacenes.map((almacen) => (
                                    <MenuItem key={almacen.codAlmacen} value={almacen.codAlmacen}>
                                        {almacen.codAlmacen} - {almacen.desAlmacen}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid container size={{ xs: 12, md: 4 }}>
                        <FormControl fullWidth>
                            <InputLabel>Tipo maquina</InputLabel>
                            <Select label="Tipo maquina" value={filtroTipoMaquina} onChange={(event) => setFiltroTipoMaquina(event.target.value)}>
                                <MenuItem value="todos">Todos</MenuItem>
                                {tiposMaquinas.map((tipoMaquina) => (
                                    <MenuItem key={tipoMaquina.codTipoMaquina} value={tipoMaquina.codTipoMaquina}>
                                        {tipoMaquina.codTipoMaquina} - {tipoMaquina.desTipoMaquina}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid container size={{ xs: 12, md: 4 }}>
                        <FormControl fullWidth>
                            <InputLabel>Estado</InputLabel>
                            <Select label="Estado" value={filtroActiva} onChange={(event) => setFiltroActiva(event.target.value)}>
                                <MenuItem value="todos">Todas</MenuItem>
                                <MenuItem value="activa">Activas</MenuItem>
                                <MenuItem value="inactiva">Inactivas</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                {loading ? (
                    <LoadingMaestro label="Cargando salidas de produccion..." />
                ) : (
                <>
                <Grid container direction="row" size={{ xs: 12 }} sx={{ justifyContent: "start", alignItems: "stretch", mt: 1 }} spacing={2}>
                    {salidasFiltradas.map((salida) => (
                        <Grid key={salida.codMaquina} container direction="column" size={{ xs: 12, md: 6, xl: 4 }} className='card-main' sx={{ p: 2, gap: 1.5, justifyContent: "space-between" }}>
                            <Grid container direction="row" size={{ xs: 12 }} sx={{ justifyContent: "space-between", alignItems: "start", gap: 1 }}>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="h6" sx={{ lineHeight: 1.15 }}>
                                        {salida.desMaquina}
                                    </Typography>

                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        Codigo maquina: {salida.codMaquina}
                                    </Typography>
                                </Box>

                                <Chip
                                    size="small"
                                    label={salida.activa ? 'Activa' : 'Inactiva'}
                                    color={salida.activa ? 'success' : 'default'}
                                    variant={salida.activa ? 'filled' : 'outlined'}
                                />
                            </Grid>

                            <Grid container direction="row" size={{ xs: 12 }} sx={{ gap: 1 }}>
                                <Chip
                                    size="small"
                                    label={`${salida.codAlmacen} - ${salida.almacenes?.desAlmacen || almacenesPorCodigo[salida.codAlmacen] || 'Almacen'}`}
                                    variant="outlined"
                                />

                                <Chip
                                    size="small"
                                    label={salida.tipos_maquinas?.desTipoMaquina || tiposPorCodigo[salida.tipoMaquina] || `Tipo ${salida.tipoMaquina}`}
                                    color="primary"
                                    variant="outlined"
                                />
                            </Grid>

                            <Grid container direction="row" size={{ xs: 12 }} sx={{ alignItems: "center", gap: 1 }}>
                                <SortIcon color="primary" />
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Orden visual: {salida.ordenVisual ?? '-'}
                                </Typography>
                            </Grid>

                            <Grid container direction="row" size={{ xs: 12 }} sx={{ justifyContent: "end", gap: 1 }}>
                                <Button variant="outlined" size="small" onClick={() => abrirEditarSalida(salida)}>
                                    <EditIcon />&nbsp;Editar
                                </Button>

                                <Button variant="outlined" color="error" size="small" onClick={() => setSalidaEliminar(salida)}>
                                    <DeleteForeverIcon />&nbsp;Eliminar
                                </Button>
                            </Grid>
                        </Grid>
                    ))}

                    {salidasFiltradas.length === 0 && (
                        <Grid container direction="column" size={{ xs: 12 }} className='card-main' sx={{ p: 3, alignItems: "center" }}>
                            <OutputIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
                            <Typography variant="h6">
                                No hay salidas de produccion
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Ajusta los filtros o crea una nueva salida de produccion.
                            </Typography>
                        </Grid>
                    )}
                </Grid>
                </>
                )}
            </Grid>

            <Dialog open={openSalida} onClose={cerrarSalida} fullWidth maxWidth="sm">
                <form onSubmit={guardarSalida}>
                    <DialogTitle>
                        <Grid container direction="row" size={{ xs: 12 }} sx={{ justifyContent: "space-between", alignItems: "center" }}>
                            <Typography variant="h5">
                                {salidaEditandoCod ? 'Editar salida produccion' : 'Nueva salida produccion'}
                            </Typography>

                            <Button variant="text" color="negro" onClick={cerrarSalida}>
                                <CloseIcon />
                            </Button>
                        </Grid>
                    </DialogTitle>

                    <DialogContent>
                        <Grid container direction="row" size={{ xs: 12 }} sx={{ justifyContent: "center", alignItems: "center", pt: 1 }} spacing={2}>
                            <Grid container size={{ xs: 12 }}>
                                <TextField
                                    label="Descripcion"
                                    name="desMaquina"
                                    value={salidaForm.desMaquina}
                                    onChange={handleChangeSalida}
                                    fullWidth
                                    required
                                />
                            </Grid>

                            <Grid container size={{ xs: 12, sm: 6 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Almacen</InputLabel>
                                    <Select
                                        label="Almacen"
                                        name="codAlmacen"
                                        value={salidaForm.codAlmacen}
                                        onChange={handleChangeSalida}
                                        required
                                    >
                                        {almacenes.map((almacen) => (
                                            <MenuItem key={almacen.codAlmacen} value={almacen.codAlmacen}>
                                                {almacen.codAlmacen} - {almacen.desAlmacen}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid container size={{ xs: 12, sm: 6 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Tipo maquina</InputLabel>
                                    <Select
                                        label="Tipo maquina"
                                        name="tipoMaquina"
                                        value={salidaForm.tipoMaquina}
                                        onChange={handleChangeSalida}
                                        required
                                    >
                                        {tiposMaquinas.map((tipoMaquina) => (
                                            <MenuItem key={tipoMaquina.codTipoMaquina} value={tipoMaquina.codTipoMaquina}>
                                                {tipoMaquina.codTipoMaquina} - {tipoMaquina.desTipoMaquina}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid container size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Orden visual"
                                    name="ordenVisual"
                                    type="number"
                                    value={salidaForm.ordenVisual}
                                    onChange={handleChangeSalida}
                                    fullWidth
                                    required
                                    inputProps={{ min: 0 }}
                                />
                            </Grid>

                            <Grid container direction="column" size={{ xs: 12, sm: 6 }} sx={{ alignItems: "start" }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="activa"
                                            checked={Boolean(salidaForm.activa)}
                                            onChange={handleChangeSalida}
                                        />
                                    }
                                    label="Activa"
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>

                    <DialogActions>
                        <Button color="primary" onClick={cerrarSalida}>
                            Cancelar
                        </Button>

                        <Button color="primary" variant="contained" type="submit">
                            {salidaEditandoCod ? 'Guardar salida' : 'Crear salida'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            <Dialog open={Boolean(salidaEliminar)} onClose={() => setSalidaEliminar(null)} fullWidth maxWidth="xs">
                <DialogTitle>
                    Eliminar salida produccion
                </DialogTitle>

                <DialogContent>
                    <Typography>
                        Desea eliminar la salida {salidaEliminar?.desMaquina || ''}?
                    </Typography>
                </DialogContent>

                <DialogActions>
                    <Button color="primary" onClick={() => setSalidaEliminar(null)}>
                        Cancelar
                    </Button>

                    <Button color="error" variant="contained" onClick={confirmarEliminarSalida}>
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    )
}
